import { DateService } from '@lemon/core/service/date.service'
import { KopioijaPalvelu } from '../kopioija.service'
import { LaskuSharedService } from './lasku-shared.service'

import {
  AsiakkaanTuote,
  AsiakkaanTuoteLaskussa,
  AsiakkaanTuoteWithoutKey,
  EmailStatus,
  Lasku,
  Laskuasetukset,
  LaskuBase,
  LaskunAlv,
  LaskunAsiakas,
  LaskunLahetystyyppi,
  LaskunLiitetiedosto,
  LaskunTila,
  LaskunTuote,
  LaskunTyypit,
  LaskunumeroTyyppi,
  LaskuPrintStatus,
  LaskuSahkoinenStatus,
  LaskutettavatPalvelut,
  LaskuToimittaja,
  TuoteAvaimet,
  UlkoinenLaskuHyvitysTaiMuistutus,
  UUDEN_LASKUN_AVAIN,
} from '../../model/lasku'

import { LocalDate, NumberDate, Timestamp } from '@lemon/core/model/common'

import { ValuuttakonversionTiedot } from '@lemon/core-firebase/model/tiliote'
import { tuotenimikkeet } from '@lemon/core-firebase/model/tuotenimikkeet'
import { TimestampProviderBase } from '@lemon/core/service/timestamp-provider.interface'
import { VahvistettuKorkoService } from '../vahvistettu-korko.service'
import { LaskuKorkoService } from './lasku-korko.service'

export interface OssProvider {
  onkoLaskunLuonutAsiakasOssRekisterissa: () => Promise<boolean>
}

const KORJAUSTUOTE_MAP: Partial<Record<LaskutettavatPalvelut, LaskutettavatPalvelut>> = {
  [LaskutettavatPalvelut.JATKUVA_KUUKAUSILASKUTUS]: LaskutettavatPalvelut.JATKUVA_KUUKAUSILASKUTUS_KORJAUS,
  [LaskutettavatPalvelut.MAKSUT]: LaskutettavatPalvelut.MAKSUT_KORJAUS,
  [LaskutettavatPalvelut.SAHKOISTEN_LASKUJEN_VASTAANOTTO]: LaskutettavatPalvelut.SAHKOISTEN_LASKUJEN_VASTAANOTTO_KORJAUS,
}

export class LaskuKopioija {
  constructor(
    private dateService: DateService,
    private timestampService: TimestampProviderBase,
    private kopioijaPalvelu: KopioijaPalvelu,
    private shared: LaskuSharedService,
    private _korkoService: LaskuKorkoService,
    private vahvistettuKorkoService: VahvistettuKorkoService,
  ) {}

  private valmisteleUusiLasku(lasku: LaskuBase): LaskuBase {
    lasku.lukossa = false
    lasku.email = null
    lasku.print = null
    lasku.nro = null
    lasku.avain = UUDEN_LASKUN_AVAIN
    return lasku
  }

  public annaUusiLaskuIlmanKorkoprosenttia(): Lasku {
    const juurilasku: Lasku = {
      avain: null,
      asiakas: null,
      nro: null,
      pvm: null,
      pvml: null,
      p: null,
      erapvm: null,
      erapvml: null,
      erap: null,
      toimituspvm: null,
      toimituspvml: null,
      toimitusp: null,
      viitteenne: null,
      lisatiedot: null,
      tyyppi: null,
      nrotyyppi: LaskunumeroTyyppi.TAVALLINEN,
      kieli: 'fi',
      valuutta: null,
      tuotteet: null,
      date: null,
      lukossa: true,
      email: null,
      print: null,
      sahkoinen: null,
      avoinna: 0,
      avoinnaRypas: 0,
      summa: 0,
      summaAlv0: 0,
      summaRypas: 0,
      summaRypasAlv0: 0,
      summaHyvityksetLaskunPvm: 0,
      summaReskontraLaskunPvm: 0,
      tila: LaskunTila.avoin,
      liittyvatLaskut: {},
      // reskontra: [],
      liitteet: [],
      lahetystyyppi: LaskunLahetystyyppi.KERTA,
      viivastyskorkoprosentti: null,
      perintatiedot: null,
      asetukset: null,
      luoja: null,
      luotu: null,
      paivitetty: null,
      paivittaja: null,
    }

    this.valmisteleUusiLasku(juurilasku)

    // Defaults
    const pvm = this.timestampService.now()
    const erapvm = this.timestampService.lisaaPaiviaTimestamp(pvm, 14)

    const localNow = this.dateService.timestampToLocalDate(pvm)
    const localEraNow = this.dateService.timestampToLocalDate(erapvm)

    juurilasku.toimituspvm = pvm
    juurilasku.toimituspvml = localNow
    juurilasku.toimitusp = this.dateService.localDateToNumber(localNow)

    juurilasku.date = pvm

    juurilasku.pvm = pvm
    juurilasku.pvml = localNow
    juurilasku.p = this.dateService.localDateToNumber(localNow)

    juurilasku.erapvm = erapvm
    juurilasku.erapvml = localEraNow
    juurilasku.erap = this.dateService.localDateToNumber(localEraNow)

    juurilasku.valuutta = 'EUR'
    juurilasku.tyyppi = LaskunTyypit.TAVALLINEN.avain
    juurilasku.kieli = 'fi'

    juurilasku.asiakas = this.copyAsiakas(this.annaUusiAsiakas())
    juurilasku.tuotteet = []

    return juurilasku
  }

  public async annaUusiLasku(): Promise<Lasku> {
    const juurilasku = this.annaUusiLaskuIlmanKorkoprosenttia()
    juurilasku.viivastyskorkoprosentti = await this._korkoService.annaOletusViivastyskorkoProsenttiNumber()
    return juurilasku
  }

  public annaUusiTuote(): AsiakkaanTuote {
    return {
      $key: null,
      nimi: '',
      hinta: 0.0,
      date: null,
      alv: {},
    }
  }

  public annaUusiToimittaja(): LaskuToimittaja {
    return {
      avain: null,

      numero: null,
      nimi: '',
      maksunSaaja: null,
      ytunnus: null,
      tunnisteetData: [],
      tunnisteet: [],
      iban: {},

      luoja: null,
      luotu: null,
      paivitetty: null,
      paivittaja: null,
      poistettu: false,
    }
  }

  public annaUusiAsiakas(): LaskunAsiakas {
    return {
      avain: null,
      nimi: null,
      ytunnus: null,
      katuosoite: null,
      postitmp: null,
      postinro: null,
      maa: 'FIN',
      date: null,
      laskunVastaanottajat: [],
      laskunKieli: null,
      laskunTyyppi: null,
      laskunValuutta: null,
      sahkoinenosoite: {
        sahkoinenOsoite: null,
        sahkoinenValittaja: null,
      },
      asiakastyyppi: null,
      viimeisinLaskuLahetetty: null,
    }
  }

  private paivitaLaskunPaivamaara(lasku: LaskuBase): Timestamp {
    // Asetetaan laskun päivämääräksi nykyinen
    const nytPvm = this.timestampService.now()
    const asLocalDate = this.dateService.timestampToLocalDate(nytPvm)

    lasku.pvm = nytPvm
    lasku.pvml = asLocalDate
    lasku.p = this.dateService.localDateToNumber(asLocalDate)

    return nytPvm
  }

  paivitaLaskunPaivamaaraJaErapaiva(lasku: LaskuBase) {
    // Lasketaan paljonko on maksuaikaa
    const maksuaikaa = this.shared.laskeMaksuaikaa(lasku)
    this.paivitaLaskunPaivamaaraJaErapaivaMaaralla(lasku, maksuaikaa)
  }

  asetaUudenLaskunMaksuaika(lasku: LaskuBase, maksuaikaa: number) {
    this.paivitaLaskunPaivamaaraJaErapaivaMaaralla(lasku, maksuaikaa)
  }

  private paivitaLaskunPaivamaaraJaErapaivaMaaralla(lasku: LaskuBase, maksuaikaa: number) {
    // Asetetaan laskun päivämääräksi nykyinen
    const nytPvm = this.paivitaLaskunPaivamaara(lasku)

    // Siirretään eräpäivää eteenpäin jos tarvis
    const erapaivaUusi = this.timestampService.lisaaPaiviaTimestamp(nytPvm, maksuaikaa)

    lasku.erapvm = erapaivaUusi
    lasku.erapvml = this.dateService.timestampToLocalDate(lasku.erapvm)
    lasku.erap = this.dateService.localDateToNumber(lasku.erapvml)
  }

  public laskePaivamaaratJaErapaivaPaivamaaralla(p: NumberDate, lasku: LaskuBase) {
    const maksuaikaa = this.shared.laskeMaksuaikaa(lasku)
    const pvml = this.dateService.numberToLocalDate(p)
    const pvm = this.timestampService.localDateToTimestamp(pvml)

    const erap = this.dateService.lisaaPaiviaNumber(p, maksuaikaa)
    const erapvml = this.dateService.numberToLocalDate(erap)
    const erapvm = this.timestampService.localDateToTimestamp(erapvml)

    return {
      pvm: pvm,
      pvml: pvml,
      p: p,
      toimituspvm: pvm,
      toimituspvml: pvml,
      toimitusp: p,
      erapvm: erapvm,
      erapvml: erapvml,
      erap: erap,
      date: pvm,
    }
  }

  private kopioiMuokattavaksiIlmanPaivamaarienKorjauksia(lasku: LaskuBase): LaskuBase {
    const kopioitu = this.copyLaskuBase(lasku)
    const valmisteltu = this.valmisteleUusiLasku(kopioitu)

    valmisteltu.date = this.timestampService.now()

    // Poista huomautuskulu ja korko
    if (valmisteltu.tuotteet) {
      valmisteltu.tuotteet = valmisteltu.tuotteet.filter(laskunTuote => {
        return !laskunTuote.tuote || (laskunTuote.tuote.avain !== TuoteAvaimet.HUOMAUTUSKULU && laskunTuote.tuote.avain !== TuoteAvaimet.KORKOKULU)
      })
    }

    // Tyhjennä aina luotu, luoja, paivitetty ja paivittaja kentät
    kopioitu.luotu = null
    kopioitu.luoja = null
    kopioitu.paivitetty = null
    kopioitu.paivittaja = null

    return valmisteltu
  }

  private kopioiMuokattavaksiToistuvaLasku(lasku: Lasku): Lasku {
    const kopioitu = this.copyLasku(lasku)

    // Valmistele uusi lasku
    kopioitu.avain = UUDEN_LASKUN_AVAIN
    kopioitu.lukossa = false
    kopioitu.nro = null

    // Tyhjennä aina luotu, luoja, paivitetty ja paivittaja kentät
    kopioitu.luotu = null
    kopioitu.luoja = null
    kopioitu.paivitetty = null
    kopioitu.paivittaja = null

    return kopioitu
  }

  private kopioiMuokattavaksi(lasku: LaskuBase): LaskuBase {
    const valmisteltu = this.kopioiMuokattavaksiIlmanPaivamaarienKorjauksia(lasku)
    this.paivitaLaskunPaivamaaraJaErapaiva(valmisteltu)
    return valmisteltu
  }

  public kopioiEiLahetettyMuokattavaksi(juurilasku: Lasku, kasiteltava: LaskuBase, laskunAsiakas: LaskunAsiakas): { muokattavaJuuri: Lasku; muokattavaKasiteltava: LaskuBase } {
    const muokattavaksiJuuri = this.copyLasku(juurilasku)
    const muokattavaksiKasiteltava = this.shared.annaKasiteltavaLasku(muokattavaksiJuuri, kasiteltava.avain)
    this.paivitaAsiakkaanTiedotKasiteltavassa(muokattavaksiKasiteltava, laskunAsiakas)
    this.paivitaLaskunPaivamaaraJaErapaiva(muokattavaksiKasiteltava)
    return {
      muokattavaJuuri: muokattavaksiJuuri,
      muokattavaKasiteltava: muokattavaksiKasiteltava,
    }
  }

  public kopioiLaskuMuokattavaksi(lasku: LaskuBase, laskunAsiakas: LaskunAsiakas): LaskuBase {
    const kopioitu = this.kopioiMuokattavaksi(lasku)
    kopioitu.nrotyyppi = LaskunumeroTyyppi.KORJAUS

    this.paivitaAsiakkaanTiedotKasiteltavassa(kopioitu, laskunAsiakas)

    return kopioitu
  }

  public kopioiToistuvaLaskuMuokattavaksi(lasku: Lasku, laskunAsiakas: LaskunAsiakas, toistuvaLuonnosAvain?: string): Lasku {
    const kopioitu = this.kopioiMuokattavaksiToistuvaLasku(lasku)

    kopioitu.toistuvaLaskuaihio = false
    kopioitu.valuuttakonversio = null
    if (toistuvaLuonnosAvain) {
      kopioitu.toistoasetustenAvain = toistuvaLuonnosAvain
    }
    this.paivitaAsiakkaanTiedotKasiteltavassa(kopioitu, laskunAsiakas)

    return kopioitu
  }

  public kopioiLaskuHyvitettavaksi(lasku: LaskuBase, laskunAsiakas: LaskunAsiakas): LaskuBase {
    const kopioitu = this.kopioiLaskuMuokattavaksi(lasku, laskunAsiakas)

    if (kopioitu.tuotteet) {
      for (const tuote of kopioitu.tuotteet) {
        tuote.maara = 0 - tuote.maara
        if (typeof tuote.yhteensa === 'number') {
          tuote.yhteensa = 0 - tuote.yhteensa
        }
      }
    }

    this.paivitaAsiakkaanTiedotKasiteltavassa(kopioitu, laskunAsiakas)

    kopioitu.nrotyyppi = LaskunumeroTyyppi.HYVITYS
    kopioitu.liitteet = []

    return kopioitu
  }

  public async kopioiUusiLasku(juuriLasku: Lasku, lasku: LaskuBase, laskunAsiakas: LaskunAsiakas, ossProvider: OssProvider): Promise<Lasku> {
    const uusi = await this.annaUusiLasku()

    uusi.kieli = lasku.kieli
    uusi.lisatiedot = lasku.lisatiedot
    uusi.tuotteet = lasku.tuotteet
    uusi.tyyppi = lasku.tyyppi
    uusi.valuutta = lasku.valuutta
    uusi.viitteenne = lasku.viitteenne

    const maksuaikaa = this.dateService.paiviaValissaPaikallinen(lasku.erapvml, lasku.pvml)
    uusi.erap = this.dateService.lisaaPaiviaNumber(uusi.p, maksuaikaa)
    uusi.erapvml = this.dateService.numberToLocalDate(uusi.erap)
    uusi.erapvm = this.timestampService.dateToTimestamp(this.dateService.localDateToDate(uusi.erapvml))

    const entisenLaskunOletusViivastyskorkoprosentti = await this.vahvistettuKorkoService.annaViivästyskorkoProsentti(lasku.pvml)
    const entinenLaskuKayttiOletusViivastyskorkoprosenttia = juuriLasku.viivastyskorkoprosentti === entisenLaskunOletusViivastyskorkoprosentti

    if (entinenLaskuKayttiOletusViivastyskorkoprosenttia) {
      const nykyinenOletusKorkoprosentti = await this.vahvistettuKorkoService.annaViivästyskorkoProsentti(uusi.pvml)
      uusi.viivastyskorkoprosentti = nykyinenOletusKorkoprosentti
    } else {
      uusi.viivastyskorkoprosentti = juuriLasku.viivastyskorkoprosentti
    }

    const kopioitu = this.copyLasku(uusi)

    if (kopioitu.tyyppi === LaskunTyypit.EU_KULUTTAJA.avain) {
      const ossRekisterissa = await ossProvider.onkoLaskunLuonutAsiakasOssRekisterissa()
      if (ossRekisterissa) {
        kopioitu.tyyppi = LaskunTyypit.EU_KULUTTAJA_PALVELU.avain
      } else {
        kopioitu.tyyppi = LaskunTyypit.EU_KULUTTAJA_EI_REKISTEROITYNYT.avain
      }
    }

    if (kopioitu.tuotteet) {
      kopioitu.tuotteet = kopioitu.tuotteet.filter(tuote => {
        return tuote.tuote.avain !== TuoteAvaimet.HUOMAUTUSKULU && tuote.tuote.avain !== TuoteAvaimet.KORKOKULU
      })

      for (const tuote of kopioitu.tuotteet) {
        this._flipAlvPercentagesInLaskuntuoteBasedOnDate(tuote, kopioitu.p)
      }
    }

    this.paivitaAsiakkaanTiedotKasiteltavassa(kopioitu, laskunAsiakas)

    return kopioitu
  }

  flipAlvIfNeededBasedOnDate(alv: LaskunAlv, date: NumberDate): LaskunAlv {
    if (alv?.tunniste === 'suomi24') {
      return {
        prosentti: 25.5,
        tunniste: 'suomi255',
        d: 1,
        s: 240901,
      }
    } else if (date > LaskuKopioija.suomi14To135CutoffDate && alv?.tunniste === 'suomi14') {
      return {
        prosentti: 13.5,
        tunniste: 'suomi135',
        s: this.dateService.lisaaPaiviaNumber(LaskuKopioija.suomi14To135CutoffDate, 1),
      }
    }
    return alv
  }

  /**
   * 31.8.2024 jälkeen kaikki suomi24% alvit muutetaan suomi255%:ksi
   */
  public static suomi24To255CutoffDate: NumberDate = 240831
  public static suomi14To135CutoffDate: NumberDate = 251231
  private _flipAlvPercentagesInLaskuntuoteBasedOnDate(tuote: LaskunTuote, date: NumberDate) {
    if (!tuote?.alv) {
      return
    }
    const uusiAlv = this.flipAlvIfNeededBasedOnDate(tuote.alv, date)
    if (uusiAlv !== tuote.alv) {
      tuote.alv = uusiAlv
      const verollisenRivinHinnat = this.shared.annaLaskurivinHinnatVerollisesta(tuote)
      if (verollisenRivinHinnat) {
        tuote.yhteensa = verollisenRivinHinnat.yhteensa
        tuote.hinta = verollisenRivinHinnat.hinta
      } else {
        delete tuote.hinnanTyyppi
        delete tuote.yhteensa
      }
      tuote.tuote.date = this.timestampService.now()
      if (tuote.tuote?.alv) {
        for (const key of Object.keys(tuote.tuote.alv)) {
          if (tuote.tuote.alv[key] === tuote.alv.tunniste) {
            tuote.tuote.alv[key] = uusiAlv.tunniste
          }
        }
      }
    }
  }

  public paivitaAsiakkaanTiedotKasiteltavassa(kasiteltava: LaskuBase, laskunAsiakas: LaskunAsiakas) {
    if (kasiteltava && laskunAsiakas) {
      if (!kasiteltava.asiakas) {
        kasiteltava.asiakas = {} as LaskunAsiakas
      }

      kasiteltava.asiakas.avain = this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.avain)
      kasiteltava.asiakas.date = this.timestampService.now()
      kasiteltava.asiakas.katuosoite = this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.katuosoite)
      kasiteltava.asiakas.laskunKieli = this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.laskunKieli)
      kasiteltava.asiakas.laskunTyyppi = this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.laskunTyyppi)
      kasiteltava.asiakas.laskunValuutta = this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.laskunValuutta)

      kasiteltava.asiakas.laskunVastaanottajat = []
      if (laskunAsiakas.laskunVastaanottajat) {
        for (const vastaanottaja of laskunAsiakas.laskunVastaanottajat) {
          kasiteltava.asiakas.laskunVastaanottajat.push(vastaanottaja)
        }
      }

      kasiteltava.asiakas.maa = this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.maa)
      kasiteltava.asiakas.nimi = this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.nimi)
      kasiteltava.asiakas.postinro = this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.postinro)
      kasiteltava.asiakas.postitmp = this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.postitmp)
      kasiteltava.asiakas.ytunnus = this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.ytunnus)
      kasiteltava.asiakas.asiakastyyppi = this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.asiakastyyppi)
      kasiteltava.asiakas.viimeisinLaskuLahetetty = this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.viimeisinLaskuLahetetty)

      if (laskunAsiakas.poistettu) {
        kasiteltava.asiakas.poistettu = true
      } else {
        delete kasiteltava.asiakas.poistettu
      }

      if (laskunAsiakas.laskutusprofiiliAvain) {
        kasiteltava.asiakas.laskutusprofiiliAvain = laskunAsiakas.laskutusprofiiliAvain
      } else {
        delete kasiteltava.asiakas.laskutusprofiiliAvain
      }

      if (laskunAsiakas.oletusLahetystapa) {
        kasiteltava.asiakas.oletusLahetystapa = laskunAsiakas.oletusLahetystapa
      } else {
        delete kasiteltava.asiakas.oletusLahetystapa
      }

      if (laskunAsiakas.maksuaikaa !== undefined && laskunAsiakas.maksuaikaa !== null) {
        kasiteltava.asiakas.maksuaikaa = laskunAsiakas.maksuaikaa
      } else {
        delete kasiteltava.asiakas.maksuaikaa
      }

      if (laskunAsiakas.sahkoinenosoite) {
        kasiteltava.asiakas.sahkoinenosoite = {
          sahkoinenOsoite: this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.sahkoinenosoite.sahkoinenOsoite),
          sahkoinenValittaja: this.kopioijaPalvelu.nullTaiArvo(laskunAsiakas.sahkoinenosoite.sahkoinenValittaja),
        }
      } else {
        kasiteltava.asiakas.sahkoinenosoite = null
      }
    }
  }

  public kopioiLaskuMuistutusLaskuksi(
    juurilasku: Lasku,
    lasku: LaskuBase,
    laskunAsiakas: LaskunAsiakas,
    alvt: LaskunAlv[],
    maksuaikaa: number = 5,
  ): { kopioitu: LaskuBase; korkokulu: LaskunTuote; huomautuskulu: LaskunTuote } {
    const edellinenMuistutus = this.shared.annaViimeisinMuistutuslasku(juurilasku)

    let alv: LaskunAlv = {
      prosentti: 0,
      tunniste: 'huomautuskulukorkoalv',
    }

    if (alvt) {
      for (const alvi of alvt) {
        if (alvi.prosentti.toFixed(2) === '0.00') {
          alv = alvi
          break
        }
      }
    }

    const vanhatHuomautuskulut = []
    if (edellinenMuistutus && edellinenMuistutus.tuotteet) {
      for (const vanhaTuote of edellinenMuistutus.tuotteet) {
        if (vanhaTuote.tuote.avain === TuoteAvaimet.HUOMAUTUSKULU) {
          vanhatHuomautuskulut.push(vanhaTuote)
        }
      }
    }

    const kopioitu = this.kopioiMuokattavaksiIlmanPaivamaarienKorjauksia(lasku)
    this.paivitaLaskunPaivamaaraJaErapaivaMaaralla(kopioitu, maksuaikaa)
    kopioitu.nrotyyppi = LaskunumeroTyyppi.MUISTUTUS

    const korkokulu: LaskunTuote = {
      alv: alv,
      hinta: 0,
      maara: 1,
      ale: 0,
      tuote: {
        avain: TuoteAvaimet.KORKOKULU,
        date: this.timestampService.now(),
        hinta: 0,
        nimi: this.shared.annaLokalisoituMerkkijono('lasku.viivastyskorko-tuote', lasku),
        alv: {},
      },
    }

    const huomautuskulu: LaskunTuote = {
      alv: alv,
      hinta: 0,
      maara: 1,
      ale: 0,
      tuote: {
        avain: TuoteAvaimet.HUOMAUTUSKULU,
        date: this.timestampService.now(),
        hinta: 0,
        nimi: this.shared.annaLokalisoituMerkkijono('lasku.huomautuskulu-tuote', lasku, { numero: vanhatHuomautuskulut.length + 1 }),
        alv: {},
      },
    }

    for (const vanhaHuomautuskulu of vanhatHuomautuskulut) {
      kopioitu.tuotteet.push(vanhaHuomautuskulu)
    }

    kopioitu.tuotteet.push(huomautuskulu)
    kopioitu.tuotteet.push(korkokulu)

    this.paivitaAsiakkaanTiedotKasiteltavassa(kopioitu, laskunAsiakas)

    return {
      huomautuskulu: huomautuskulu,
      kopioitu: kopioitu,
      korkokulu: korkokulu,
    }
  }

  public copyAsetukset(asetukset: Laskuasetukset): Laskuasetukset {
    return this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<Laskuasetukset>(asetukset)
  }

  kopioiTaiPoista(prop: keyof Omit<Lasku, keyof LaskuBase>, source: Lasku, target: Lasku) {
    if (source[prop] === undefined) {
      delete target[prop]
    } else {
      ;(target as any)[prop] = this.kopioijaPalvelu.nullTaiArvo(source[prop])
    }
  }

  kopioiTaiPoistaObjekti(prop: keyof Omit<Lasku, keyof LaskuBase>, source: Lasku, target: Lasku) {
    if (source[prop] === undefined) {
      delete target[prop]
    } else {
      ;(target as any)[prop] = this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville(source[prop])
    }
  }

  public siirraLaskuTiedot(source: Lasku, target: Lasku): void {
    this.siirraLaskuBasenTiedot(source, target)

    const resultMap: Lasku['liittyvatLaskut'] = target.liittyvatLaskut ?? {}
    for (const [avain, liittyvaLasku] of Object.entries(source.liittyvatLaskut ?? {})) {
      if (resultMap[avain]) {
        this.siirraLaskuBasenTiedot(liittyvaLasku, resultMap[avain])
      } else {
        resultMap[avain] = this.copyLaskuBase(liittyvaLasku)
      }
    }
    target.liittyvatLaskut = resultMap

    // Lasku
    this.kopioiTaiPoista('tila', source, target)
    this.kopioiTaiPoista('viitenumero', source, target)
    this.kopioiTaiPoista('avoinnaRypas', source, target)
    this.kopioiTaiPoista('summaRypas', source, target)
    this.kopioiTaiPoista('summaRypasAlv0', source, target)
    this.kopioiTaiPoista('lahetystyyppi', source, target)
    this.kopioiTaiPoista('viivastyskorkoprosentti', source, target)
    this.kopioiTaiPoista('kommentti', source, target)
    this.kopioiTaiPoista('eraantynytViestiLahetetty', source, target)
    this.kopioiTaiPoista('omitFromSearches', source, target)
    this.kopioiTaiPoista('listauksessa', source, target)
    this.kopioiTaiPoista('omitFromStatistics', source, target)
    this.kopioiTaiPoistaObjekti('perintatiedot', source, target)
  }

  public copyLasku(lasku: Lasku): Lasku {
    const copied: Lasku = {
      // Base
      avain: this.kopioijaPalvelu.nullTaiArvo(lasku.avain),
      lukossa: this.kopioijaPalvelu.nullTaiArvo(lasku.lukossa),
      asiakas: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LaskunAsiakas>(lasku.asiakas),
      nro: this.kopioijaPalvelu.nullTaiArvo(lasku.nro),
      nrotyyppi: this.kopioijaPalvelu.nullTaiArvo(lasku.nrotyyppi),
      pvm: this.kopioijaPalvelu.nullTaiArvo(lasku.pvm),
      pvml: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LocalDate>(lasku.pvml),
      p: this.kopioijaPalvelu.nullTaiArvo(lasku.p),
      erapvm: this.kopioijaPalvelu.nullTaiArvo(lasku.erapvm),
      erapvml: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LocalDate>(lasku.erapvml),
      erap: this.kopioijaPalvelu.nullTaiArvo(lasku.erap),
      toimituspvm: this.kopioijaPalvelu.nullTaiArvo(lasku.toimituspvm),
      toimituspvml: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LocalDate>(lasku.toimituspvml),
      toimitusp: this.kopioijaPalvelu.nullTaiArvo(lasku.toimitusp),
      viitteenne: this.kopioijaPalvelu.nullTaiArvo(lasku.viitteenne),
      lisatiedot: this.kopioijaPalvelu.nullTaiArvo(lasku.lisatiedot),
      tyyppi: this.kopioijaPalvelu.nullTaiArvo(lasku.tyyppi),
      kieli: this.kopioijaPalvelu.nullTaiArvo(lasku.kieli),
      valuutta: this.kopioijaPalvelu.nullTaiArvo(lasku.valuutta),
      tuotteet: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LaskunTuote[]>(lasku.tuotteet),
      date: this.kopioijaPalvelu.nullTaiArvo(lasku.date),
      summa: this.kopioijaPalvelu.nullTaiArvo(lasku.summa),
      summaAlv0: this.kopioijaPalvelu.nullTaiArvo(lasku.summaAlv0),
      summaHyvityksetLaskunPvm: this.kopioijaPalvelu.nullTaiArvo(lasku.summaHyvityksetLaskunPvm),
      summaReskontraLaskunPvm: this.kopioijaPalvelu.nullTaiArvo(lasku.summaReskontraLaskunPvm),
      avoinna: this.kopioijaPalvelu.nullTaiArvo(lasku.avoinna),
      email: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<EmailStatus>(lasku.email),
      print: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LaskuPrintStatus>(lasku.print),
      sahkoinen: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LaskuSahkoinenStatus>(lasku.sahkoinen),
      liitteet: this.kopioiLaskunLiitteet(lasku.liitteet),
      asetukset: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<Laskuasetukset>(lasku.asetukset),
      luoja: this.kopioijaPalvelu.nullTaiArvo(lasku.luoja),
      luotu: this.kopioijaPalvelu.nullTaiArvo(lasku.luotu),
      paivitetty: this.kopioijaPalvelu.nullTaiArvo(lasku.paivitetty),
      paivittaja: this.kopioijaPalvelu.nullTaiArvo(lasku.paivittaja),

      // Lasku
      avoinnaRypas: this.kopioijaPalvelu.nullTaiArvo(lasku.avoinnaRypas),
      summaRypas: this.kopioijaPalvelu.nullTaiArvo(lasku.summaRypas),
      summaRypasAlv0: this.kopioijaPalvelu.nullTaiArvo(lasku.summaRypasAlv0),
      tila: this.kopioijaPalvelu.nullTaiArvo(lasku.tila),
      liittyvatLaskut: this._copyLaskuBasetMappiin(lasku.liittyvatLaskut),
      viitenumero: this.kopioijaPalvelu.nullTaiArvo(lasku.viitenumero),
      lahetystyyppi: this.kopioijaPalvelu.nullTaiArvo(lasku.lahetystyyppi),
      viivastyskorkoprosentti: this.kopioijaPalvelu.nullTaiArvo(lasku.viivastyskorkoprosentti),
    }
    if (lasku.tilastot) {
      copied.tilastot = this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville(lasku.tilastot)
    }
    if (lasku.tagTilitapahtumat) {
      copied.tagTilitapahtumat = this.kopioijaPalvelu.cloneArrayDeep<string>(lasku.tagTilitapahtumat)
    }
    if (lasku.ulkoinenLaskunro !== undefined) {
      copied.ulkoinenLaskunro = lasku.ulkoinenLaskunro
    }
    if (lasku.ulkoinenLaskunroLajittelu !== undefined) {
      copied.ulkoinenLaskunroLajittelu = lasku.ulkoinenLaskunroLajittelu
    }
    if (lasku.ulkoinenLaskuHyvitysTaiMuistutus !== undefined) {
      copied.ulkoinenLaskuHyvitysTaiMuistutus = this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<UlkoinenLaskuHyvitysTaiMuistutus>(lasku.ulkoinenLaskuHyvitysTaiMuistutus)
    }
    if (lasku.valuuttakonversio !== undefined) {
      copied.valuuttakonversio = this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<ValuuttakonversionTiedot>(lasku.valuuttakonversio)
    }
    if (lasku.kommentti) {
      copied.kommentti = lasku.kommentti
    }
    if (lasku.eraantynytViestiLahetetty !== undefined) {
      copied.eraantynytViestiLahetetty = lasku.eraantynytViestiLahetetty
    }

    if (lasku.omitFromSearches !== undefined) {
      copied.omitFromSearches = lasku.omitFromSearches
    }
    if (lasku.listauksessa !== undefined) {
      copied.listauksessa = lasku.listauksessa
    }
    if (lasku.omitFromStatistics !== undefined) {
      copied.omitFromStatistics = lasku.omitFromStatistics
    }
    if (lasku.source !== undefined) {
      copied.source = lasku.source
    }
    if (lasku.sourceId !== undefined) {
      copied.sourceId = lasku.sourceId
    }
    if (lasku.lisaViestit !== undefined) {
      copied.lisaViestit = this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville(lasku.lisaViestit)
    }
    if (lasku.ennakkolasku !== undefined) {
      copied.ennakkolasku = lasku.ennakkolasku
    }
    if (lasku.kateislasku !== undefined) {
      copied.kateislasku = lasku.kateislasku
    }
    if (lasku.toistuvaLaskuaihio !== undefined) {
      copied.toistuvaLaskuaihio = lasku.toistuvaLaskuaihio
    }
    if (lasku.toistoasetustenAvain !== undefined) {
      copied.toistoasetustenAvain = lasku.toistoasetustenAvain
    }

    if (lasku.perintatiedot) {
      copied.perintatiedot = {
        lahetettyPeruutusFrontend: lasku.perintatiedot.lahetettyPeruutusFrontend,
        lahetettyPeruutus: lasku.perintatiedot.lahetettyPeruutus,
        lahetettyFrontend: lasku.perintatiedot.lahetettyFrontend,
        lahetetty: lasku.perintatiedot.lahetetty,
        lahetykset: this.kopioijaPalvelu.cloneDeep(lasku.perintatiedot.lahetykset),
        vastaanottaja: lasku.perintatiedot.vastaanottaja,
        status: lasku.perintatiedot.status,
        success: lasku.perintatiedot.success,
      }
    }
    return copied
  }

  private kopioiLaskunLiitteet(liitteet: LaskunLiitetiedosto[]): LaskunLiitetiedosto[] {
    if (liitteet) {
      const uudetLiitteet: LaskunLiitetiedosto[] = []
      for (const lahde of liitteet) {
        uudetLiitteet.push({
          avain: this.kopioijaPalvelu.nullTaiArvo(lahde.avain),
          fileEnding: this.kopioijaPalvelu.nullTaiArvo(lahde.fileEnding),
          koko: this.kopioijaPalvelu.nullTaiArvo(lahde.koko),
          nimi: this.kopioijaPalvelu.nullTaiArvo(lahde.nimi),
          lisatty: this.kopioijaPalvelu.nullTaiArvo(lahde.lisatty),
          lisattyl: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville(lahde.lisattyl),
        })
      }
      return uudetLiitteet
    }
    return null
  }

  private _copyLaskuBasetMappiin(mapit: Lasku['liittyvatLaskut']): Lasku['liittyvatLaskut'] {
    if (!mapit) {
      return {}
    }
    const output: Lasku['liittyvatLaskut'] = {}
    for (const base of Object.values(mapit)) {
      output[base.avain] = this.copyLaskuBase(base)
    }
    return output
  }

  kopioiTaiPoistaBase(prop: keyof LaskuBase, source: LaskuBase, target: LaskuBase) {
    if (source[prop] === undefined) {
      delete target[prop]
    } else {
      ;(target as any)[prop] = this.kopioijaPalvelu.nullTaiArvo(source[prop])
    }
  }

  kopioiTaiPoistaBaseObjekti(prop: keyof LaskuBase, source: LaskuBase, target: LaskuBase) {
    if (source[prop] === undefined) {
      delete target[prop]
    } else {
      ;(target as any)[prop] = this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville(source[prop])
    }
  }

  public siirraLaskuBasenTiedot(source: LaskuBase, target: LaskuBase): void {
    // Base
    this.kopioiTaiPoistaBase('avain', source, target)
    this.kopioiTaiPoistaBase('lukossa', source, target)
    this.kopioiTaiPoistaBaseObjekti('asiakas', source, target)
    this.kopioiTaiPoistaBase('nro', source, target)
    this.kopioiTaiPoistaBase('nrotyyppi', source, target)
    this.kopioiTaiPoistaBase('pvm', source, target)
    this.kopioiTaiPoistaBaseObjekti('pvml', source, target)
    this.kopioiTaiPoistaBase('p', source, target)
    this.kopioiTaiPoistaBase('erapvm', source, target)
    this.kopioiTaiPoistaBaseObjekti('erapvml', source, target)
    this.kopioiTaiPoistaBase('erap', source, target)
    this.kopioiTaiPoistaBase('toimituspvm', source, target)
    this.kopioiTaiPoistaBaseObjekti('toimituspvml', source, target)
    this.kopioiTaiPoistaBase('toimitusp', source, target)
    this.kopioiTaiPoistaBase('viitteenne', source, target)
    this.kopioiTaiPoistaBase('lisatiedot', source, target)
    this.kopioiTaiPoistaBase('tyyppi', source, target)
    this.kopioiTaiPoistaBase('kieli', source, target)
    this.kopioiTaiPoistaBase('valuutta', source, target)
    this.kopioiTaiPoistaBaseObjekti('tuotteet', source, target)
    this.kopioiTaiPoistaBase('date', source, target)
    this.kopioiTaiPoistaBase('summa', source, target)
    this.kopioiTaiPoistaBase('summaAlv0', source, target)
    this.kopioiTaiPoistaBase('summaHyvityksetLaskunPvm', source, target)
    this.kopioiTaiPoistaBase('summaReskontraLaskunPvm', source, target)
    this.kopioiTaiPoistaBase('avoinna', source, target)
    this.kopioiTaiPoistaBaseObjekti('email', source, target)
    this.kopioiTaiPoistaBaseObjekti('print', source, target)
    this.kopioiTaiPoistaBaseObjekti('sahkoinen', source, target)

    const liitteet = this.kopioiLaskunLiitteet(source.liitteet)
    if (liitteet === undefined) {
      delete target['liitteet']
    } else {
      target['liitteet'] = liitteet
    }

    this.kopioiTaiPoistaBaseObjekti('asetukset', source, target)
    this.kopioiTaiPoistaBase('luoja', source, target)
    this.kopioiTaiPoistaBase('luotu', source, target)
    this.kopioiTaiPoistaBase('paivitetty', source, target)
    this.kopioiTaiPoistaBase('paivittaja', source, target)

    this.kopioiTaiPoistaBase('tagTilitapahtumat', source, target)

    this.kopioiTaiPoistaBase('source', source, target)
    this.kopioiTaiPoistaBase('sourceId', source, target)

    this.kopioiTaiPoistaBase('ulkoinenLaskunro', source, target)

    this.kopioiTaiPoistaBase('ulkoinenLaskunroLajittelu', source, target)

    this.kopioiTaiPoistaBaseObjekti('ulkoinenLaskuHyvitysTaiMuistutus', source, target)

    this.kopioiTaiPoistaBaseObjekti('valuuttakonversio', source, target)

    this.kopioiTaiPoistaBaseObjekti('tilastot', source, target)
  }

  public copyLaskuBase(lasku: LaskuBase): LaskuBase {
    const copied: LaskuBase = {
      // Base
      avain: this.kopioijaPalvelu.nullTaiArvo(lasku.avain),
      lukossa: this.kopioijaPalvelu.nullTaiArvo(lasku.lukossa),
      asiakas: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LaskunAsiakas>(lasku.asiakas),
      nro: this.kopioijaPalvelu.nullTaiArvo(lasku.nro),
      nrotyyppi: this.kopioijaPalvelu.nullTaiArvo(lasku.nrotyyppi),
      pvm: this.kopioijaPalvelu.nullTaiArvo(lasku.pvm),
      pvml: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LocalDate>(lasku.pvml),
      p: this.kopioijaPalvelu.nullTaiArvo(lasku.p),
      erapvm: this.kopioijaPalvelu.nullTaiArvo(lasku.erapvm),
      erapvml: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LocalDate>(lasku.erapvml),
      erap: this.kopioijaPalvelu.nullTaiArvo(lasku.erap),
      toimituspvm: this.kopioijaPalvelu.nullTaiArvo(lasku.toimituspvm),
      toimituspvml: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LocalDate>(lasku.toimituspvml),
      toimitusp: this.kopioijaPalvelu.nullTaiArvo(lasku.toimitusp),
      viitteenne: this.kopioijaPalvelu.nullTaiArvo(lasku.viitteenne),
      lisatiedot: this.kopioijaPalvelu.nullTaiArvo(lasku.lisatiedot),
      tyyppi: this.kopioijaPalvelu.nullTaiArvo(lasku.tyyppi),
      kieli: this.kopioijaPalvelu.nullTaiArvo(lasku.kieli),
      valuutta: this.kopioijaPalvelu.nullTaiArvo(lasku.valuutta),
      tuotteet: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LaskunTuote[]>(lasku.tuotteet),
      date: this.kopioijaPalvelu.nullTaiArvo(lasku.date),
      summa: this.kopioijaPalvelu.nullTaiArvo(lasku.summa),
      summaAlv0: this.kopioijaPalvelu.nullTaiArvo(lasku.summaAlv0),
      summaHyvityksetLaskunPvm: this.kopioijaPalvelu.nullTaiArvo(lasku.summaHyvityksetLaskunPvm),
      summaReskontraLaskunPvm: this.kopioijaPalvelu.nullTaiArvo(lasku.summaReskontraLaskunPvm),
      avoinna: this.kopioijaPalvelu.nullTaiArvo(lasku.avoinna),
      email: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<EmailStatus>(lasku.email),
      print: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LaskuPrintStatus>(lasku.print),
      sahkoinen: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<LaskuSahkoinenStatus>(lasku.sahkoinen),
      liitteet: this.kopioiLaskunLiitteet(lasku.liitteet),
      asetukset: this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<Laskuasetukset>(lasku.asetukset),
      luoja: this.kopioijaPalvelu.nullTaiArvo(lasku.luoja),
      luotu: this.kopioijaPalvelu.nullTaiArvo(lasku.luotu),
      paivitetty: this.kopioijaPalvelu.nullTaiArvo(lasku.paivitetty),
      paivittaja: this.kopioijaPalvelu.nullTaiArvo(lasku.paivittaja),
    }
    if (lasku.tagTilitapahtumat) {
      copied.tagTilitapahtumat = this.kopioijaPalvelu.cloneArrayDeep<string>(lasku.tagTilitapahtumat)
    }
    if (lasku.source !== undefined) {
      copied.source = this.kopioijaPalvelu.nullTaiArvo(lasku.source)
    }
    if (lasku.sourceId !== undefined) {
      copied.sourceId = this.kopioijaPalvelu.nullTaiArvo(lasku.sourceId)
    }
    if (lasku.ulkoinenLaskunro !== undefined) {
      copied.ulkoinenLaskunro = this.kopioijaPalvelu.nullTaiArvo(lasku.ulkoinenLaskunro)
    }
    if (lasku.ulkoinenLaskunroLajittelu !== undefined) {
      copied.ulkoinenLaskunroLajittelu = this.kopioijaPalvelu.nullTaiArvo(lasku.ulkoinenLaskunroLajittelu)
    }
    if (lasku.ulkoinenLaskuHyvitysTaiMuistutus !== undefined) {
      copied.ulkoinenLaskuHyvitysTaiMuistutus = this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<UlkoinenLaskuHyvitysTaiMuistutus>(lasku.ulkoinenLaskuHyvitysTaiMuistutus)
    }
    if (lasku.valuuttakonversio !== undefined) {
      copied.valuuttakonversio = this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville<ValuuttakonversionTiedot>(lasku.valuuttakonversio)
    }
    if (lasku.tilastot !== undefined) {
      copied.tilastot = this.kopioijaPalvelu.kopioiJaAsetaNullPuuttuville(lasku.tilastot)
    }

    return copied
  }

  getDateOrNull(raw: string): Date | null {
    if (raw) {
      return new Date(raw)
    }
    return null
  }

  // prepareLaskunAsiakas(asiakas: LaskunAsiakasLaskussa): LaskunAsiakasLaskussa {
  //   return {
  //     avain: this.valueOrNull(asiakas.avain),
  //     nimi: this.valueOrNull(asiakas.nimi),
  //     ytunnus: this.valueOrNull(asiakas.ytunnus),
  //     katuosoite: this.valueOrNull(asiakas.katuosoite),
  //     postitmp: this.valueOrNull(asiakas.postitmp),
  //     postinro: this.valueOrNull(asiakas.postinro),
  //     maa: this.valueOrNull(asiakas.maa),
  //     date: this.valueOrNull(asiakas.date),
  //     laskunVastaanottajat: this.valueOrNull(asiakas.laskunVastaanottajat)
  //   }
  // }

  // muunnaLaskunAsiakasAsiakkaaksi(asiakas: LaskunAsiakasLaskussa): LaskunAsiakas {
  //   return {
  //     $key: this.valueOrNull(asiakas.avain),
  //     nimi: this.valueOrNull(asiakas.nimi),
  //     ytunnus: this.valueOrNull(asiakas.ytunnus),
  //     katuosoite: this.valueOrNull(asiakas.katuosoite),
  //     postitmp: this.valueOrNull(asiakas.postitmp),
  //     postinro: this.valueOrNull(asiakas.postinro),
  //     maa: this.valueOrNull(asiakas.maa),
  //     date: this.valueOrNull(asiakas.date),
  //     laskunVastaanottajat: this.valueOrNull(asiakas.laskunVastaanottajat),
  //     laskunValuutta: this.valueOrNull(asiakas.laskunValuutta),
  //     laskunTyyppi: this.valueOrNull(asiakas.laskunTyyppi),
  //     laskunKieli: this.valueOrNull(asiakas.laskunKieli)
  //   }
  // }

  // copyAsiakasLaskuun(asiakas: LaskunAsiakas): LaskunAsiakasLaskussa {
  //   return {
  //     avain: this.valueOrNull(asiakas.$key),
  //     nimi: this.valueOrNull(asiakas.nimi),
  //     ytunnus: this.valueOrNull(asiakas.ytunnus),
  //     katuosoite: this.valueOrNull(asiakas.katuosoite),
  //     postitmp: this.valueOrNull(asiakas.postitmp),
  //     postinro: this.valueOrNull(asiakas.postinro),
  //     maa: this.valueOrNull(asiakas.maa),
  //     date: this.valueOrNull(asiakas.date),
  //     laskunVastaanottajat: this.valueOrNull(asiakas.laskunVastaanottajat),
  //     laskunValuutta: this.valueOrNull(asiakas.laskunValuutta),
  //     laskunTyyppi: this.valueOrNull(asiakas.laskunTyyppi),
  //     laskunKieli: this.valueOrNull(asiakas.laskunKieli)
  //   }
  // }

  public copyAsiakas(asiakas: LaskunAsiakas): LaskunAsiakas {
    const palautettava: LaskunAsiakas = {
      avain: this.valueOrNull(asiakas.avain),
      nimi: this.valueOrNull(asiakas.nimi),
      ytunnus: this.valueOrNull(asiakas.ytunnus),
      katuosoite: this.valueOrNull(asiakas.katuosoite),
      postitmp: this.valueOrNull(asiakas.postitmp),
      postinro: this.valueOrNull(asiakas.postinro),
      maa: this.valueOrNull(asiakas.maa),
      date: this.valueOrNull(asiakas.date),
      laskunVastaanottajat: this.valueOrNull(asiakas.laskunVastaanottajat),
      laskunValuutta: this.valueOrNull(asiakas.laskunValuutta),
      laskunTyyppi: this.valueOrNull(asiakas.laskunTyyppi),
      laskunKieli: this.valueOrNull(asiakas.laskunKieli),
      sahkoinenosoite: this.kopioijaPalvelu.cloneDeep(asiakas.sahkoinenosoite),
      asiakastyyppi: this.valueOrNull(asiakas.asiakastyyppi),
      viimeisinLaskuLahetetty: this.valueOrNull(asiakas.viimeisinLaskuLahetetty),
    }

    if (asiakas.poistettu !== undefined) {
      palautettava.poistettu = asiakas.poistettu
    }
    if (asiakas.oletusLahetystapa !== undefined) {
      palautettava.oletusLahetystapa = asiakas.oletusLahetystapa
    }
    if (asiakas.maksuaikaa !== undefined) {
      palautettava.maksuaikaa = asiakas.maksuaikaa
    }
    // CSV/XLSX-tuonnista tuleva ulkoinen tunniste — säilytetään vain jos lähtöasiakkaalla on arvo,
    // ettei normaalille lemonaid-asiakkaalle synny turhia null-kenttiä Firestoreen.
    if (asiakas.ulkoinenTunniste !== undefined) {
      palautettava.ulkoinenTunniste = asiakas.ulkoinenTunniste
    }
    if (asiakas.laskutusprofiiliAvain) {
      palautettava.laskutusprofiiliAvain = asiakas.laskutusprofiiliAvain
    }

    return palautettava
  }

  // public copyAsiakasMuokattavaksi(asiakas: LaskunAsiakas): LaskunAsiakas {
  //   return {
  //     $key: this.valueOrNull(asiakas.$key),
  //     nimi: this.valueOrNull(asiakas.nimi),
  //     ytunnus: this.valueOrNull(asiakas.ytunnus),
  //     katuosoite: this.valueOrNull(asiakas.katuosoite),
  //     postitmp: this.valueOrNull(asiakas.postitmp),
  //     postinro: this.valueOrNull(asiakas.postinro),
  //     maa: this.valueOrNull(asiakas.maa),
  //     date: this.valueOrNull(asiakas.date),
  //     laskunVastaanottajat: this.valueOrNull(asiakas.laskunVastaanottajat),
  //     laskunValuutta: this.valueOrNull(asiakas.laskunValuutta),
  //     laskunTyyppi: this.valueOrNull(asiakas.laskunTyyppi),
  //     laskunKieli: this.valueOrNull(asiakas.laskunKieli)
  //   }
  // }

  private valueOrNull(value: any): any {
    return value ? value : null
  }

  copyTuoteLaskuun(tuote: AsiakkaanTuote): AsiakkaanTuoteLaskussa {
    if (!tuote) {
      return {
        avain: null,
        nimi: null,
        hinta: null,
        date: null,
        alv: null,
      }
    }
    const kopio: AsiakkaanTuoteLaskussa = {
      avain: this.valueOrNull(tuote.$key),
      nimi: this.valueOrNull(tuote.nimi),
      hinta: this.valueOrNull(tuote.hinta),
      date: this.valueOrNull(tuote.date),
      alv: this.valueOrNull(tuote.alv),
    }
    if (tuote.yksikko !== undefined) {
      kopio.yksikko = tuote.yksikko
    }
    if (tuote.valuutta !== undefined) {
      kopio.valuutta = tuote.valuutta
    }
    if (tuote.verottomuudenPeruste !== undefined) {
      kopio.verottomuudenPeruste = tuote.verottomuudenPeruste
    }
    return kopio
  }

  copyTuoteLaskusta(tuote: AsiakkaanTuoteLaskussa): AsiakkaanTuote {
    if (!tuote) {
      return {
        $key: null,
        nimi: null,
        hinta: null,
        date: null,
        alv: null,
      }
    }
    const kopio: AsiakkaanTuote = {
      $key: this.valueOrNull(tuote.avain),
      nimi: this.valueOrNull(tuote.nimi),
      hinta: this.valueOrNull(tuote.hinta),
      date: this.valueOrNull(tuote.date),
      alv: this.valueOrNull(tuote.alv),
    }
    if (tuote.yksikko !== undefined) {
      kopio.yksikko = tuote.yksikko
    }
    if (tuote.valuutta !== undefined) {
      kopio.valuutta = tuote.valuutta
    }
    if (tuote.verottomuudenPeruste !== undefined) {
      kopio.verottomuudenPeruste = tuote.verottomuudenPeruste
    }
    return kopio
  }

  public teeAsiakkaanTuoteLaskuntuotteesta(laskunTuote: LaskunTuote): AsiakkaanTuote {
    const asiakkaanTuote: AsiakkaanTuote = {
      $key: laskunTuote.tuote.avain,
      date: laskunTuote.tuote.date,
      hinta: laskunTuote.tuote.hinta,
      nimi: laskunTuote.tuote.nimi,
      alv: laskunTuote.tuote.alv,
    }
    if (laskunTuote.yksikko) {
      asiakkaanTuote.yksikko = laskunTuote.yksikko
    }
    if (laskunTuote.tuote.valuutta) {
      asiakkaanTuote.valuutta = laskunTuote.tuote.valuutta
    }
    const verottomuudenPeruste = laskunTuote.verottomuudenPeruste ?? laskunTuote.tuote.verottomuudenPeruste
    if (verottomuudenPeruste) {
      asiakkaanTuote.verottomuudenPeruste = verottomuudenPeruste
    }
    return asiakkaanTuote
  }

  public copyTuote(tuote: AsiakkaanTuoteLaskussa | AsiakkaanTuote): AsiakkaanTuoteWithoutKey {
    const kopio: AsiakkaanTuoteWithoutKey = {
      nimi: this.valueOrNull(tuote.nimi),
      hinta: this.valueOrNull(tuote.hinta),
      date: this.valueOrNull(tuote.date),
      alv: this.valueOrNull(tuote.alv),
    }
    // CSV-tuonnista tulevat metakentät — säilytetään vain jos lähtötuotteessa oli arvo, ettei normaalille
    // lemonaid-tuotteelle synny turhia null-kenttiä Firestoreen
    if (tuote.ulkoinenTunniste !== undefined) {
      kopio.ulkoinenTunniste = tuote.ulkoinenTunniste
    }
    if (tuote.yksikko !== undefined) {
      kopio.yksikko = tuote.yksikko
    }
    if (tuote.kuvaus !== undefined) {
      kopio.kuvaus = tuote.kuvaus
    }
    if (tuote.valuutta !== undefined) {
      kopio.valuutta = tuote.valuutta
    }
    if (tuote.verottomuudenPeruste !== undefined) {
      kopio.verottomuudenPeruste = tuote.verottomuudenPeruste
    }
    if (tuote.poistettu !== undefined) {
      kopio.poistettu = tuote.poistettu
    }
    return kopio
  }

  public copyTuoteMuokattavaksi(tuote: AsiakkaanTuote): AsiakkaanTuote {
    const kopio: AsiakkaanTuote = {
      $key: this.valueOrNull(tuote.$key),
      nimi: this.valueOrNull(tuote.nimi),
      hinta: this.valueOrNull(tuote.hinta),
      date: this.valueOrNull(tuote.date),
      alv: this.valueOrNull(tuote.alv),
    }
    if (tuote.ulkoinenTunniste !== undefined) {
      kopio.ulkoinenTunniste = tuote.ulkoinenTunniste
    }
    if (tuote.yksikko !== undefined) {
      kopio.yksikko = tuote.yksikko
    }
    if (tuote.kuvaus !== undefined) {
      kopio.kuvaus = tuote.kuvaus
    }
    if (tuote.valuutta !== undefined) {
      kopio.valuutta = tuote.valuutta
    }
    if (tuote.verottomuudenPeruste !== undefined) {
      kopio.verottomuudenPeruste = tuote.verottomuudenPeruste
    }
    return kopio
  }

  public copyToimittaja(toimittaja: LaskuToimittaja): LaskuToimittaja {
    const kopio: LaskuToimittaja = {
      avain: this.valueOrNull(toimittaja.avain),

      numero: toimittaja.numero,
      nimi: this.valueOrNull(toimittaja.nimi) || '',
      maksunSaaja: this.valueOrNull(toimittaja.maksunSaaja),
      ytunnus: this.valueOrNull(toimittaja.ytunnus),
      tunnisteetData: this.copyToimittajaTunnisteetData(toimittaja.tunnisteetData),
      tunnisteet: this.copyToimittajaTunnisteet(toimittaja.tunnisteet),
      iban: this.copyToimittajaIban(toimittaja.iban),

      luoja: this.valueOrNull(toimittaja.luoja),
      luotu: this.valueOrNull(toimittaja.luotu),
      paivitetty: this.valueOrNull(toimittaja.paivitetty),
      paivittaja: this.valueOrNull(toimittaja.paivittaja),
      poistettu: toimittaja.poistettu ? true : false,
    }
    if (toimittaja.katuosoite) {
      kopio.katuosoite = toimittaja.katuosoite
    }
    if (toimittaja.postinro) {
      kopio.postinro = toimittaja.postinro
    }
    if (toimittaja.postitmp) {
      kopio.postitmp = toimittaja.postitmp
    }
    if (toimittaja.kanava) {
      kopio.kanava = toimittaja.kanava
    }
    return kopio
  }

  public copyToimittajaIban(iban: LaskuToimittaja['iban']): LaskuToimittaja['iban'] {
    const copiedIban: LaskuToimittaja['iban'] = {}
    for (const [ibanValue, data] of Object.entries(iban || {})) {
      copiedIban[ibanValue] = {
        iban: this.valueOrNull(data.iban),
        bic: this.valueOrNull(data.bic),
        tyyppi: this.valueOrNull(data.tyyppi),
      }
    }
    return copiedIban
  }

  public copyToimittajaTunnisteetData(tunnisteetData: LaskuToimittaja['tunnisteetData']): LaskuToimittaja['tunnisteetData'] {
    return (tunnisteetData ?? []).map(tunniste => ({
      tyyppi: this.valueOrNull(tunniste.tyyppi),
      arvo: this.valueOrNull(tunniste.arvo),
      ...(tunniste.maakoodi ? { maakoodi: this.valueOrNull(tunniste.maakoodi) } : {}),
    }))
  }

  public copyToimittajaTunnisteet(tunnisteet: LaskuToimittaja['tunnisteet']): LaskuToimittaja['tunnisteet'] {
    return [...(tunnisteet ?? [])]
  }

  public korvaaJatkuvatTuotteetKorjaustuotteilla(lasku: LaskuBase): void {
    if (lasku.tuotteet) {
      for (const tuote of lasku.tuotteet) {
        if (tuote.tuote?.avain && KORJAUSTUOTE_MAP[tuote.tuote.avain]) {
          const uusiAvain = KORJAUSTUOTE_MAP[tuote.tuote.avain]
          if (tuotenimikkeet.find(t => t.avain === uusiAvain)) {
            tuote.tuote.avain = uusiAvain
          }
        }
      }
    }
  }

  // public getDate(date: Date): string {
  //   return date.toJSON()
  // }
}
